import React, { Fragment, ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';

export interface ModalProps {
    open?: boolean;
    isOpen?: boolean;
    onClose: () => void;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    closeOnOverlayClick?: boolean;
    showCloseButton?: boolean;
    title?: string;
    description?: string;
    children: ReactNode;
}

export const Modal = ({
    open: controlledOpen,
    isOpen,
    onClose,
    size = 'md',
    closeOnOverlayClick = true,
    showCloseButton = true,
    title,
    description,
    children,
}: ModalProps) => {
    const open = controlledOpen ?? isOpen ?? false;
    const sizeStyles = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        full: 'max-w-7xl',
    };

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-50"
                onClose={closeOnOverlayClick ? onClose : () => { }}
            >
                {/* Backdrop */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
                </Transition.Child>

                {/* Modal Container */}
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel
                                className={cn(
                                    'w-full transform overflow-hidden rounded-2xl',
                                    'bg-white dark:bg-gray-800',
                                    'p-6 text-left align-middle shadow-2xl transition-all',
                                    sizeStyles[size]
                                )}
                            >
                                {/* Close Button */}
                                {showCloseButton && (
                                    <button
                                        onClick={onClose}
                                        className="absolute top-4 right-4 p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                )}

                                {(title || description) && (
                                    <ModalHeader>
                                        {title && <ModalTitle>{title}</ModalTitle>}
                                        {description && <ModalDescription>{description}</ModalDescription>}
                                    </ModalHeader>
                                )}

                                {children}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export interface ModalHeaderProps {
    children: ReactNode;
    className?: string;
}

export const ModalHeader = ({ children, className }: ModalHeaderProps) => {
    return (
        <div className={cn('mb-4 pr-8', className)}>
            {children}
        </div>
    );
};

export interface ModalTitleProps {
    children: ReactNode;
    className?: string;
}

export const ModalTitle = ({ children, className }: ModalTitleProps) => {
    return (
        <Dialog.Title
            as="h3"
            className={cn(
                'text-xl font-semibold text-gray-900 dark:text-gray-100',
                className
            )}
        >
            {children}
        </Dialog.Title>
    );
};

export interface ModalDescriptionProps {
    children: ReactNode;
    className?: string;
}

export const ModalDescription = ({ children, className }: ModalDescriptionProps) => {
    return (
        <Dialog.Description
            className={cn('mt-2 text-sm text-gray-500 dark:text-gray-400', className)}
        >
            {children}
        </Dialog.Description>
    );
};

export interface ModalBodyProps {
    children: ReactNode;
    className?: string;
}

export const ModalBody = ({ children, className }: ModalBodyProps) => {
    return (
        <div className={cn('mt-4', className)}>
            {children}
        </div>
    );
};

export interface ModalFooterProps {
    children: ReactNode;
    className?: string;
}

export const ModalFooter = ({ children, className }: ModalFooterProps) => {
    return (
        <div className={cn('mt-6 flex items-center justify-end gap-3', className)}>
            {children}
        </div>
    );
};

export default Modal;
